import React, { useEffect, useState } from 'react';
import { dealerBilling } from '../../services/dealerApi';
import { MessageCircle, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { formatUtcDate } from '../../utils/formatUtcDate';

export default function BillingCard() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    dealerBilling.usage()
      .then((u) => {
        setUsage(u);
        setStartDate(u.billing_start_date || '');
      })
      .catch(() => toast.error('Failed to load usage'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const saveStartDate = async () => {
    if (!startDate) return;
    setSaving(true);
    try {
      const u = await dealerBilling.updateSettings({ billing_start_date: startDate });
      setUsage(u);
      toast.success('Billing cycle updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-5 rounded-2xl bg-slate-900/40 border border-slate-800 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-cyan-500 animate-spin" />
      </div>
    );
  }
  if (!usage) return null;

  const pct = Math.min(100, (usage.sent / Math.max(usage.cap, 1)) * 100);
  const barColor = usage.blocked ? 'bg-red-500' : usage.warning ? 'bg-amber-500' : 'bg-cyan-500';

  return (
    <div className="p-5 rounded-2xl bg-slate-900/40 border border-slate-800 space-y-4" data-testid="billing-card">
      <div className="flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-cyan-400" />
        <h3 className="text-lg font-bold text-white">WhatsApp Usage</h3>
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <div className="text-3xl font-extrabold text-white tabular-nums" data-testid="billing-sent-count">
            {usage.sent.toLocaleString()}
            <span className="text-lg text-slate-500"> / {usage.cap.toLocaleString()}</span>
          </div>
          <div className={`text-xs font-bold ${usage.blocked ? 'text-red-300' : usage.warning ? 'text-amber-300' : 'text-slate-400'}`}>
            {usage.blocked ? 'CAP REACHED' : usage.warning ? 'Approaching limit' : `${usage.remaining.toLocaleString()} left`}
          </div>
        </div>
        <div className="mt-2 h-2 bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 text-xs text-slate-500">
          Cycle: {formatUtcDate(usage.cycle_start)} – {formatUtcDate(usage.cycle_end)}
        </div>
      </div>

      <div className="pt-3 border-t border-slate-800">
        <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Billing cycle start date</label>
        <div className="flex gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="billing-start-date-input"
            className="flex-1 px-3 py-2 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm focus:border-cyan-500 outline-none"
          />
          <button
            onClick={saveStartDate}
            disabled={saving || !startDate || startDate === usage.billing_start_date}
            data-testid="billing-save-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-bold disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">
          Cap resets monthly on this day-of-month.
        </p>
      </div>
    </div>
  );
}
